import { FC, ReactNode } from "react";
import { Entity } from "@common/types/entity";
import { ConsolidatedAvatar } from "@components/avatar/ConsolidatedAvatar";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { GroupByColumnDef } from "@design-system/ViewTable/grouping/buildGroupByViewConfig";
import { HStack } from "@design-system/HStack/HStack";
import { ReportingCatalogItem, ReportingScope } from "./reportingCatalog";
import { REPORTING_COLUMN_SORT, ReportingSort } from "./reportingControls";
import styles from "./reportingGroupByConfig.module.scss";

export type ReportingGroupByColumn = "scope" | "report" | "description";

export const reportingEntityGroupByColumns: Array<
    GroupByColumnDef<
        ReportingCatalogItem,
        ReportingGroupByColumn,
        ReportingSort
    >
> = [
    {
        id: "report",
        header: "Report",
        isExpandColumn: true,
        minSize: 280,
        maxSize: 520,
        sort: REPORTING_COLUMN_SORT,
        renderItem: (item) => (
            <span className={styles.reportName}>{item.label}</span>
        ),
    },
    {
        id: "description",
        header: "Description",
        minSize: 320,
        maxSize: 1600,
        renderItem: (item) => item.description,
    },
];

interface ReportingScopeIdentityProps {
    scope: ReportingScope;
    entityById: Map<number, Entity>;
    parentName: string;
}

export function renderReportingScopeIcon({
    scope,
    entityById,
    parentName,
}: ReportingScopeIdentityProps): ReactNode {
    if (scope.kind === "consolidationGroup") {
        return <ConsolidatedAvatar name={parentName} size="xs" aria-hidden />;
    }
    const entity = entityById.get(scope.id);
    return entity ? (
        <EntityAvatar entity={entity} size="sm" aria-hidden />
    ) : null;
}

export const ReportingScopeIdentity: FC<ReportingScopeIdentityProps> = ({
    scope,
    entityById,
    parentName,
}) => (
    <HStack gap={8} align="center">
        {renderReportingScopeIcon({ scope, entityById, parentName })}
        <span>{scope.label}</span>
    </HStack>
);

export function getReportingReportGroupByColumns({
    entityById,
    parentName,
}: {
    entityById: Map<number, Entity>;
    parentName: string;
}): Array<
    GroupByColumnDef<
        ReportingCatalogItem,
        ReportingGroupByColumn,
        ReportingSort
    >
> {
    return [
        {
            id: "scope",
            header: "Entity",
            isExpandColumn: true,
            minSize: 280,
            maxSize: 520,
            sort: REPORTING_COLUMN_SORT,
            renderItem: (item) => (
                <ReportingScopeIdentity
                    scope={item.scope}
                    entityById={entityById}
                    parentName={parentName}
                />
            ),
        },
        {
            id: "description",
            header: "Description",
            minSize: 320,
            maxSize: 1600,
            renderItem: (item) => item.description,
        },
    ];
}
